import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import { Input, Select, Textarea } from "@/components/base/Field";
import PhotoUploader from "@/components/base/PhotoUploader";
import { useToast } from "@/components/base/Toast";
import { getGuardians, registerEnrollment } from "@/services/studentService";
import { listFeeItems, type ApiFeeItem } from "@/services/ficheService";
import { allocateChecked, allocationError, PAYMENT_METHOD_LABEL } from "@/services/paymentService";
import { buildProvisionalReceipt, toFeeSnapshot, type ReceiptData } from "@/utils/receipt";
import FeeChecklist from "@/components/feature/FeeChecklist";
import { useAuth } from "@/contexts/AuthContext";
import { formatMoney, todayISO } from "@/utils/format";
import type {
  Cycle,
  Enrollment,
  EnrollmentStatus,
  FeeLine,
  Guardian,
  GuardianRelation,
  PaymentMethod,
  SchoolClass,
  SchoolYear,
  Student,
} from "@/types";
import { ENROLLMENT_STATUSES, ENROLLMENT_STATUS_LABEL, GUARDIAN_RELATIONS, GUARDIAN_RELATION_LABEL } from "@/utils/enrollment";
import { currencySymbol } from "@/utils/currency";

const ALL_STEPS = [
  { id: 1, label: "Élève", icon: "ri-user-3-line" },
  { id: 2, label: "Responsables", icon: "ri-parent-line" },
  { id: 3, label: "Inscription", icon: "ri-file-add-line" },
  { id: 4, label: "Versement", icon: "ri-hand-coin-line" },
];

const KIND_ORDER: Record<FeeLine["kind"], number> = { INSCRIPTION: 0, REINSCRIPTION: 0, ARTICLE: 1, TRANCHE: 2 };

/** Ce que doit l'élève d'après la fiche (même règle que le serveur), dans l'ordre de répartition. */
function duesFor(items: ApiFeeItem[], classId: string, isReenrollment: boolean): FeeLine[] {
  return items
    .filter((i) => {
      // Tranche par défaut encore à 0 FG : rien n'est dû.
      if (i.amount <= 0) return false;
      if (i.kind === "INSCRIPTION") return !isReenrollment;
      if (i.kind === "REINSCRIPTION") return isReenrollment;
      if (i.kind === "TRANCHE") return String(i.classId) === classId;
      return i.appliesToAllClasses || i.classIds.map(String).includes(classId);
    })
    .sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.position - b.position || a.id - b.id)
    .map((i) => ({
      key: String(i.id),
      enrollmentId: "",
      kind: i.kind,
      label: i.label,
      dueDate: i.dueDate ?? "",
      due: i.amount,
      paid: 0,
      remaining: i.amount,
      settlement: "IMPAYE" as const,
      applicable: true,
    }));
}

interface NewGuardian {
  key: string;
  lastName: string;
  firstName: string;
  phone: string;
  address: string;
  profession: string;
  relation: GuardianRelation;
  isPrimary: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Appelé après une inscription réussie avec l'id de l'inscription créée. */
  onEnrolled?: (enrollmentId: string, receiptNo: string | null) => void;
  /** Inscription gardée hors ligne avec un versement : reçu provisoire à imprimer. */
  onProvisional?: (data: ReceiptData) => void;
  cycles: Cycle[];
  classes: SchoolClass[];
  schoolYears: SchoolYear[];
  activeYear: string;
  presetStudent?: Student | null;
  /** Réinscription : inscriptions non annulées de l'élève (années déjà prises, classe la plus récente). */
  history?: Enrollment[];
  /** Toutes les classes de l'école (niveau de la dernière classe, même hors cycle du rôle). */
  allClasses?: SchoolClass[];
}

export default function EnrollmentWizard({
  open,
  onClose,
  onSaved,
  onEnrolled,
  onProvisional,
  cycles,
  classes,
  schoolYears,
  activeYear,
  presetStudent,
  history = [],
  allClasses = [],
}: Props) {
  const { notify } = useToast();
  const { can } = useAuth();
  // Le versement à l'inscription n'est proposé qu'à qui peut encaisser.
  const STEPS = can("payments:create") ? ALL_STEPS : ALL_STEPS.slice(0, 3);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [feeItems, setFeeItems] = useState<ApiFeeItem[] | null>(null);
  const [paidNow, setPaidNow] = useState("");
  /** Frais cochés par l'utilisateur pour le premier versement. */
  const [checkedFees, setCheckedFees] = useState<Set<string>>(new Set());
  const [method, setMethod] = useState<PaymentMethod>("ESPECES");

  const [student, setStudent] = useState({
    photo: "",
    lastName: "",
    firstName: "",
    gender: "M",
    birthDate: "",
    birthPlace: "",
    address: "",
    phone: "",
    notes: "",
  });
  const [guardianSearch, setGuardianSearch] = useState("");
  const [existing, setExisting] = useState<Guardian[]>([]);
  const [selected, setSelected] = useState<NewGuardian[]>([]);

  const [enrollment, setEnrollment] = useState({
    schoolYear: activeYear,
    classId: "",
    enrollmentDate: todayISO(),
    status: "ACTIVE" as EnrollmentStatus,
  });

  useEffect(() => {
    if (!open) return;
    getGuardians().then(setExisting);
    setStep(presetStudent ? 3 : 1);
    setStudent({
      photo: presetStudent?.photo ?? "",
      lastName: presetStudent?.lastName ?? "",
      firstName: presetStudent?.firstName ?? "",
      gender: presetStudent?.gender ?? "M",
      birthDate: presetStudent?.birthDate ?? "",
      birthPlace: presetStudent?.birthPlace ?? "",
      address: presetStudent?.address ?? "",
      phone: presetStudent?.phone ?? "",
      notes: presetStudent?.notes ?? "",
    });
    setSelected([]);
    setGuardianSearch("");
    setEnrollment({ schoolYear: activeYear, classId: "", enrollmentDate: todayISO(), status: "ACTIVE" });
    setPaidNow("");
    setCheckedFees(new Set());
    setMethod("ESPECES");
  }, [open, presetStudent, activeYear]);

  const cycleOfClass = (classId: string) => classes.find((c) => c.id === classId)?.cycleId ?? "";

  const filteredGuardians = existing.filter((g) => {
    const q = guardianSearch.trim().toLowerCase();
    if (!q) return existing.length > 0;
    return g.lastName.toLowerCase().includes(q) || g.firstName.toLowerCase().includes(q) || g.phone.includes(q);
  });

  const addExisting = (g: Guardian) => {
    if (selected.some((s) => s.key === g.id)) return;
    setSelected((p) => [
      ...p,
      {
        key: g.id,
        lastName: g.lastName,
        firstName: g.firstName,
        phone: g.phone,
        address: g.address,
        profession: g.profession,
        relation: "TUTEUR",
        isPrimary: p.length === 0,
      },
    ]);
  };

  const addNew = () => {
    setSelected((p) => [
      ...p,
      {
        key: `new-${Date.now()}`,
        lastName: "",
        firstName: "",
        phone: "",
        address: "",
        profession: "",
        relation: "PERE",
        isPrimary: p.length === 0,
      },
    ]);
  };

  const updateSelected = (key: string, patch: Partial<NewGuardian>) =>
    setSelected((p) => p.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const setPrimary = (key: string) =>
    setSelected((p) => p.map((s) => ({ ...s, isPrimary: s.key === key })));

  const removeSelected = (key: string) =>
    setSelected((p) => p.filter((s) => s.key !== key));

  const canNext = () => {
    if (step === 1) return !!student.lastName.trim() && !!student.firstName.trim();
    if (step === 3) return !!enrollment.classId;
    return true;
  };

  // Réinscription d'un élève connu : il doit la réinscription (le serveur le vérifie).
  const isReenrollment = !!presetStudent;
  const dues = feeItems && enrollment.classId ? duesFor(feeItems, enrollment.classId, isReenrollment) : [];
  const totalDue = dues.reduce((sum, l) => sum + l.due, 0);
  const paidAmount = Number(paidNow) || 0;
  const allocation = allocateChecked(dues, paidAmount, checkedFees);
  const paymentError = allocationError(allocation, paidAmount, checkedFees.size);
  const toggleFee = (key: string) =>
    setCheckedFees((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  useEffect(() => {
    if (!open || step !== 4) return;
    let alive = true;
    setFeeItems(null);
    listFeeItems(enrollment.schoolYear)
      .then((items) => alive && setFeeItems(items))
      .catch(() => alive && setFeeItems([]));
    return () => {
      alive = false;
    };
  }, [open, step, enrollment.schoolYear]);

  const handleFinish = async () => {
    if (paymentError) {
      notify(paymentError, "error");
      return;
    }
    const receiptLines = dues.map((l) => ({ feeKey: l.key, amount: allocation.amounts[l.key] ?? 0 })).filter((l) => l.amount > 0);
    setBusy(true);
    try {
      // Un seul appel transactionnel : élève + responsables + inscription + versement éventuel (un reçu).
      const who = presetStudent
        ? `${presetStudent.lastName} ${presetStudent.firstName}`
        : `${student.lastName.trim()} ${student.firstName.trim()}`;
      const registered = await registerEnrollment({
        existingStudentId: presetStudent?.id,
        student: presetStudent
          ? undefined
          : {
              lastName: student.lastName.trim(),
              firstName: student.firstName.trim(),
              gender: student.gender as Student["gender"],
              birthDate: student.birthDate,
              birthPlace: student.birthPlace,
              address: student.address,
              phone: student.phone,
              photo: student.photo,
              notes: student.notes,
            },
        guardians: selected.map((g) =>
          g.key.startsWith("new-")
            ? {
                guardian: {
                  lastName: g.lastName,
                  firstName: g.firstName,
                  phone: g.phone,
                  address: g.address,
                  profession: g.profession,
                },
                relation: g.relation,
                isPrimary: g.isPrimary,
              }
            : { guardianId: g.key, relation: g.relation, isPrimary: g.isPrimary },
        ),
        enrollment: {
          classId: enrollment.classId,
          schoolYear: enrollment.schoolYear,
          enrollmentDate: enrollment.enrollmentDate,
          status: enrollment.status,
        },
        receipt: receiptLines.length ? { date: todayISO(), method, lines: receiptLines } : undefined,
      }, {
        label: `Inscription — ${who}`,
        provisional: (ref) => {
          const schoolClass = classes.find((c) => c.id === enrollment.classId);
          return buildProvisionalReceipt({
            ref,
            date: todayISO(),
            method,
            student: {
              name: who,
              matricule: presetStudent?.matricule ?? "en attente",
              className: schoolClass?.name ?? "—",
              cycleLabel: cycles.find((c) => c.id === schoolClass?.cycleId)?.label ?? "—",
              schoolYear: enrollment.schoolYear,
            },
            fees: dues.map(toFeeSnapshot),
            amounts: allocation.amounts,
          });
        },
      });
      if ("queued" in registered) {
        // Hors ligne : gardée sur l'appareil, envoyée au retour du réseau (le matricule et le reçu officiel viendront alors).
        notify("Hors ligne : inscription gardée sur l'appareil. Elle sera envoyée au retour de la connexion.");
        if (registered.provisional) onProvisional?.(registered.provisional);
        onClose();
        return;
      }
      const { enrollment: enr, receiptNo } = registered;
      notify(receiptNo ? `Inscription enregistrée — reçu ${receiptNo}.` : "Inscription enregistrée avec succès.");
      onSaved();
      onEnrolled?.(enr.id, receiptNo);
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Erreur lors de l'enregistrement.", "error");
    } finally {
      setBusy(false);
    }
  };

  const classOptions = classes.filter((c) => c.id);
  // Réinscription : pas sur une année déjà prise ni antérieure, pas dans une classe de niveau inférieur.
  const latest = [...history].sort((a, b) => b.schoolYear.localeCompare(a.schoolYear))[0];
  const latestClass = latest ? [...allClasses, ...classes].find((c) => c.id === latest.classId) : undefined;
  const takenYears = new Set(history.map((e) => e.schoolYear));
  const yearRefused = (label: string) => takenYears.has(label) || (!!latest && label < latest.schoolYear);
  const classRefused = (cls: SchoolClass) =>
    latestClass?.level !== undefined && cls.level !== undefined && cls.level < latestClass.level;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={presetStudent ? `Réinscription — ${presetStudent.firstName} ${presetStudent.lastName}` : "Nouvelle inscription"}
      subtitle={presetStudent ? `Dossier ${presetStudent.matricule} conservé` : `Assistant en ${STEPS.length} étapes`}
      icon="ri-file-add-line"
      size="xl"
      footer={
        <>
          {step > 1 && (
            <Button variant="outline" icon="ri-arrow-left-line" onClick={() => setStep((s) => s - 1)}>
              Précédent
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          {step < STEPS.length ? (
            <Button
              icon="ri-arrow-right-line"
              disabled={!canNext()}
              onClick={() => setStep((s) => s + 1)}
            >
              Suivant
            </Button>
          ) : (
            <Button icon="ri-check-double-line" disabled={busy || !enrollment.classId || (step === 4 && (feeItems === null || !!paymentError))} onClick={handleFinish}>
              {busy ? "Enregistrement..." : "Valider l'inscription"}
            </Button>
          )}
        </>
      }
    >
      <div className="mb-6 flex items-center gap-2">
        {STEPS.map((s) => {
          const done = s.id < step;
          const active = s.id === step;
          return (
            <div key={s.id} className="flex flex-1 items-center gap-2">
              <div className="flex items-center gap-2">
                <span
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0 ${
                    active
                      ? "bg-primary-600 text-white"
                      : done
                        ? "bg-primary-100 text-primary-700"
                        : "bg-background-200 text-foreground-500"
                  }`}
                >
                  {done ? <i className="ri-check-line"></i> : <i className={s.icon}></i>}
                </span>
                <span className={`text-xs font-medium ${active ? "text-foreground-950" : "text-foreground-500"}`}>
                  {s.label}
                </span>
              </div>
              {s.id < STEPS.length && <span className="h-px flex-1 bg-background-200"></span>}
            </div>
          );
        })}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-100/50 p-4">
            <PhotoUploader
              value={student.photo}
              onChange={(v) => setStudent({ ...student, photo: v })}
              lastName={student.lastName}
              firstName={student.firstName}
              label="Photo de l'élève"
              hint="PNG, JPG ou WEBP · réduite automatiquement"
              size="md"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Matricule" value="Généré automatiquement" readOnly hint="Attribué à la création du dossier" />
          <Input label="Nom" required value={student.lastName} onChange={(e) => setStudent({ ...student, lastName: e.target.value })} />
          <Input label="Prénom" required value={student.firstName} onChange={(e) => setStudent({ ...student, firstName: e.target.value })} />
          <Select label="Sexe" value={student.gender} onChange={(e) => setStudent({ ...student, gender: e.target.value })}>
            <option value="M">Masculin</option>
            <option value="F">Féminin</option>
          </Select>
          <Input label="Date de naissance" type="date" value={student.birthDate} onChange={(e) => setStudent({ ...student, birthDate: e.target.value })} />
          <Input label="Lieu de naissance" value={student.birthPlace} onChange={(e) => setStudent({ ...student, birthPlace: e.target.value })} />
          <Input label="Adresse" value={student.address} onChange={(e) => setStudent({ ...student, address: e.target.value })} />
          <Input label="Téléphone" value={student.phone} onChange={(e) => setStudent({ ...student, phone: e.target.value })} />
          <Textarea label="Informations complémentaires" rows={2} value={student.notes} onChange={(e) => setStudent({ ...student, notes: e.target.value })} wrapperClass="sm:col-span-2" />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div className="rounded-lg border border-background-200 bg-background-100/50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                Rechercher un responsable existant
              </p>
              <Badge tone="secondary" icon="ri-search-line">
                Éviter les doublons
              </Badge>
            </div>
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                value={guardianSearch}
                onChange={(e) => setGuardianSearch(e.target.value)}
                placeholder="Nom, prénom ou téléphone du parent..."
                className="w-full bg-transparent text-sm focus:outline-none"
              />
            </div>
            <div className="mt-3 max-h-40 space-y-1.5 overflow-y-auto">
              {filteredGuardians.map((g) => (
                <div key={g.id} className="flex items-center justify-between gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm text-foreground-900 truncate">
                      {g.firstName} {g.lastName}
                    </p>
                    <p className="text-xs text-foreground-500 truncate">
                      {g.profession || "—"} • {g.phone}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" icon="ri-add-line" onClick={() => addExisting(g)}>
                    Ajouter
                  </Button>
                </div>
              ))}
              {filteredGuardians.length === 0 && (
                <p className="py-3 text-center text-xs text-foreground-400">Aucun responsable trouvé.</p>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                Responsables de l'élève ({selected.length})
              </p>
              <Button size="sm" variant="outline" icon="ri-user-add-line" onClick={addNew}>
                Nouveau responsable
              </Button>
            </div>
            {selected.map((s) => (
              <div key={s.key} className="rounded-lg border border-background-200 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <button
                    onClick={() => setPrimary(s.key)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs cursor-pointer transition-colors ${
                      s.isPrimary
                        ? "border-primary-300 bg-primary-50 text-primary-800"
                        : "border-background-300 text-foreground-500 hover:border-background-400"
                    }`}
                  >
                    <i className={s.isPrimary ? "ri-star-fill" : "ri-star-line"}></i>
                    {s.isPrimary ? "Responsable principal" : "Définir comme principal"}
                  </button>
                  <button
                    onClick={() => removeSelected(s.key)}
                    className="press w-9 h-9 sm:w-7 sm:h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-accent-50 hover:text-accent-600 cursor-pointer"
                    aria-label="Retirer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
                {s.key.startsWith("new-") ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Input label="Nom" value={s.lastName} onChange={(e) => updateSelected(s.key, { lastName: e.target.value })} />
                    <Input label="Prénom" value={s.firstName} onChange={(e) => updateSelected(s.key, { firstName: e.target.value })} />
                    <Input label="Téléphone" value={s.phone} onChange={(e) => updateSelected(s.key, { phone: e.target.value })} />
                    <Input label="Profession" value={s.profession} onChange={(e) => updateSelected(s.key, { profession: e.target.value })} />
                    <Input label="Adresse" value={s.address} onChange={(e) => updateSelected(s.key, { address: e.target.value })} />
                    <Select label="Relation" value={s.relation} onChange={(e) => updateSelected(s.key, { relation: e.target.value as GuardianRelation })}>
                      {GUARDIAN_RELATIONS.map((r) => (
                        <option key={r} value={r}>
                          {GUARDIAN_RELATION_LABEL[r]}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-medium text-foreground-900">
                      {s.firstName} {s.lastName}
                    </span>
                    <span className="text-xs text-foreground-500">{s.profession || "—"}</span>
                    <span className="text-xs text-foreground-500">{s.phone}</span>
                  </div>
                )}
              </div>
            ))}
            {selected.length === 0 && (
              <p className="rounded-md border border-dashed border-background-300 py-6 text-center text-xs text-foreground-400">
                Aucun responsable sélectionné. Recherchez un parent existant ou ajoutez-en un.
              </p>
            )}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {latest && (
            <p className="rounded-md border border-primary-200 bg-primary-50 px-3.5 py-2 text-xs text-primary-900 sm:col-span-2">
              Dernière inscription : {latest.schoolYear}
              {latestClass ? ` en ` : ""}. La réinscription se fait sur une année suivante, dans la même
              classe (redoublement) ou une classe supérieure.
            </p>
          )}
          <Select label="Année scolaire" value={enrollment.schoolYear} onChange={(e) => setEnrollment({ ...enrollment, schoolYear: e.target.value })}>
            {schoolYears.map((y) => (
              <option key={y.id} value={y.label} disabled={yearRefused(y.label)}>
                {y.label}
                {takenYears.has(y.label) ? " — déjà inscrit" : yearRefused(y.label) ? " — antérieure" : ""}
              </option>
            ))}
          </Select>
          <Select label="Classe" required value={enrollment.classId} onChange={(e) => setEnrollment({ ...enrollment, classId: e.target.value })}>
            <option value="">— Choisir une classe —</option>
            {cycles.map((c) => (
              <optgroup key={c.id} label={c.label}>
                {classOptions
                  .filter((cls) => cls.cycleId === c.id)
                  .map((cls) => (
                    <option key={cls.id} value={cls.id} disabled={classRefused(cls)}>
                      {cls.name}
                      {classRefused(cls) ? " — inférieure" : ""}
                    </option>
                  ))}
              </optgroup>
            ))}
          </Select>
          <Input label="Date d'inscription" type="date" value={enrollment.enrollmentDate} onChange={(e) => setEnrollment({ ...enrollment, enrollmentDate: e.target.value })} />
          <Select label="Statut de l'inscription" value={enrollment.status} onChange={(e) => setEnrollment({ ...enrollment, status: e.target.value as EnrollmentStatus })}>
            {ENROLLMENT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {ENROLLMENT_STATUS_LABEL[status]}
              </option>
            ))}
          </Select>
          {enrollment.classId && (
            <div className="sm:col-span-2">
              <Badge tone="primary" icon="ri-building-4-line">
                Cycle : {cycles.find((c) => c.id === cycleOfClass(enrollment.classId))?.label}
              </Badge>
            </div>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4">
          {feeItems === null ? (
            <p className="py-8 text-center text-sm text-foreground-400">Chargement de la fiche de renseignements...</p>
          ) : dues.length === 0 ? (
            <p className="rounded-md border border-dashed border-background-300 py-8 text-center text-sm text-foreground-500">
              Aucun frais défini pour cette classe en {enrollment.schoolYear} (voir « Fiche de renseignements »).
              L'inscription peut être validée sans versement.
            </p>
          ) : (
            <>
              <p className="text-xs text-foreground-600">
                Total de l'année ({isReenrollment ? "réinscription" : "nouvel élève"}) : <strong>{formatMoney(totalDue)}</strong>. Cochez les
                frais que ce premier versement règle, puis saisissez le montant.
              </p>
              <FeeChecklist lines={dues} checked={checkedFees} onToggle={toggleFee} allocation={allocation} amount={paidAmount} dueLabel="Montant" />
              {paymentError && (
                <p role="alert" className="animate-fade-in rounded-md border border-accent-200 bg-accent-50 px-3 py-2 text-sm text-accent-800">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {paymentError}
                </p>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input
                  label={`Montant versé maintenant (${currencySymbol()})`}
                  type="number"
                  min={0}
                  value={paidNow}
                  onChange={(e) => setPaidNow(e.target.value)}
                  hint="Laisser vide si rien n'est versé aujourd'hui."
                />
                <Select label="Mode de paiement" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
                  {(Object.keys(PAYMENT_METHOD_LABEL) as PaymentMethod[]).map((m) => (
                    <option key={m} value={m}>
                      {PAYMENT_METHOD_LABEL[m]}
                    </option>
                  ))}
                </Select>
              </div>
              <p className="text-xs text-foreground-500">
                Le montant va sur les frais cochés ; un surplus va sur la suite de la fiche. Un reçu numéroté est émis.
                Les versements suivants se font dans « Paiements & reçus ».
              </p>
            </>
          )}
        </div>
      )}

    </Modal>
  );
}